import {
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewContainerRef,
} from '@angular/core'
import { DropdownPanel } from './dropdown-panel'
import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay'
import { TemplatePortal } from '@angular/cdk/portal'
import { merge, Observable, Subscription } from 'rxjs'

@Directive({
  standalone: true,
  selector: '[sarboDropdownTriggerFor]',
})
export class DropdownTriggerForDirective implements OnDestroy {
  public isDropdownOpen = false
  private overlayRef!: OverlayRef
  private dropdownClosingActionsSub = Subscription.EMPTY
  @Output() dropdownOpen = new EventEmitter<boolean>()

  // Permite inyectar un dropdownPanel desde el componente
  @Input('sarboDropdownTriggerFor') public dropdownPanel!: DropdownPanel

  // Permite definir una posición por defecto (opcional)
  @Input() set position(value: ConnectedPosition) {
    this.connectedPosition = value
  }

  connectedPosition: ConnectedPosition = {
    originX: 'start',
    originY: 'bottom',
    overlayX: 'start',
    overlayY: 'top',
    offsetY: 8,
  }

  constructor(
    private overlay: Overlay,
    private elementRef: ElementRef<HTMLElement>,
    private viewContainerRef: ViewContainerRef,
  ) {}

  @HostListener('click')
  toggleDropdown(): void {
    this.isDropdownOpen ? this.destroyDropdown() : this.openDropdown()
  }

  openDropdown(): void {
    // Obtiene el rectángulo del elemento disparador
    const triggerRect = this.elementRef.nativeElement.getBoundingClientRect()
    // Calcula el espacio disponible debajo y encima del elemento
    const spaceBelow = window.innerHeight - triggerRect.bottom
    const spaceAbove = triggerRect.top
    // Altura estimada que necesita el panel del dropdown. Ajusta este valor según tu diseño.
    const estimatedPanelHeight = 300

    // Calcula la posición según el espacio disponible
    let calculatedPosition: ConnectedPosition
    if (spaceBelow >= estimatedPanelHeight) {
      // Hay suficiente espacio debajo; se despliega hacia abajo
      calculatedPosition = {
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top',
        offsetY: 8,
      }
    } else if (spaceAbove >= estimatedPanelHeight) {
      // Si el espacio debajo es insuficiente pero sí hay suficiente arriba, se despliega hacia arriba
      calculatedPosition = {
        originX: 'start',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'bottom',
        offsetY: -8,
      }
    } else {
      // En caso de no tener suficiente espacio en ninguno de los dos lados, se despliega hacia abajo por defecto.
      calculatedPosition = {
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top',
        offsetY: 8,
      }
    }

    this.isDropdownOpen = true
    this.dropdownOpen.emit(this.isDropdownOpen)
    // Crea el overlay usando la posición calculada
    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      scrollStrategy: this.overlay.scrollStrategies.close(),
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.elementRef)
        .withPositions([calculatedPosition]),
    })

    const templatePortal = new TemplatePortal(this.dropdownPanel.templateRef, this.viewContainerRef)
    this.overlayRef.attach(templatePortal)

    // Se suscribe a las acciones que deben cerrar el dropdown (por ejemplo, click en el backdrop)
    this.dropdownClosingActionsSub = this.dropdownClosingActions().subscribe(() => {
      this.destroyDropdown()
    })
  }

  private dropdownClosingActions(): Observable<MouseEvent | void> {
    const backdropClick$ = this.overlayRef.backdropClick()
    const detachment$ = this.overlayRef.detachments()
    const dropdownClosed = this.dropdownPanel.closed
    return merge(backdropClick$, detachment$, dropdownClosed)
  }

  private destroyDropdown(): void {
    if (!this.overlayRef || !this.isDropdownOpen) {
      return
    }

    this.dropdownClosingActionsSub.unsubscribe()
    this.isDropdownOpen = false
    this.dropdownOpen.emit(this.isDropdownOpen)
    this.overlayRef.detach()
  }

  ngOnDestroy(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose()
    }
  }
}
